import { Parser as formulaParser } from 'hot-formula-parser';

/**
 * calculator.js - Core formula execution utility.
 * Optimized for high performance and call-safety (re-entrancy).
 */

// Singleton parser instance for maximum performance (avoids repeated object creation overhead)
const parser = new formulaParser();

// Context stack to manage overlapping or nested calculation calls.
// This ensures that variables are always resolved against the correct object
// even if one calculation triggers another.
const contextStack = [];

// Register the variable resolver once. It dynamically pulls data from the current top of the stack.
parser.on('callVariable', (name, done) => {
    const context = contextStack[contextStack.length - 1];
    if (name && context?.obj) {
        const { obj, NAValue } = context;
        const rawValue = obj[name];

        if (rawValue === undefined || rawValue === null) {
            // Handle missing values using the provided NAValue fallback
            const naNum = typeof NAValue === 'number' ? NAValue : parseFloat(NAValue);
            done(!isNaN(naNum) ? parseFloat(naNum.toFixed(4)) : 0);
        } else {
            // Resolve existing field values
            const num = parseFloat(rawValue);
            // If the value is not a number, we pass it as-is (e.g., NaN or string) 
            // to let the formula parser handle the error naturally, matching legacy behavior.
            done(!isNaN(num) ? parseFloat(num.toFixed(4)) : num);
        }
    } else {
        done(0);
    }
});

/**
 * Internal helper to run the parser within a safe stack-managed context.
 */
const runParser = (obj, formula, NAValue) => {
    if (!obj || !formula) return { error: 'Invalid input', result: null };

    contextStack.push({ obj, NAValue });
    try {
        return parser.parse(String(formula));
    } finally {
        // Ensure stack is always cleared even if parse() throws an exception
        contextStack.pop();
    }
};

export default {
    /**
     * Executes a formula and returns the raw result object { error, result }.
     */
    exec: (obj, formula, NAValue) => {
        return runParser(obj, formula, NAValue);
    },

    /**
     * Executes a formula and returns the numeric result.
     * Defaults to 0 if the result is invalid or not a number.
     * Optionally sets the result to a field in the input object.
     */
    execNum: (obj, formula, NAValue, setToField) => {
        const res = runParser(obj, formula, NAValue);
        let value = 0;

        if (res && typeof res.result === 'number' && !isNaN(res.result)) {
            value = parseFloat(res.result.toFixed(4));
        }

        if (setToField && obj) {
            obj[setToField] = value;
        }

        return value;
    },

    /**
     * Executes a formula and returns a boolean result.
     * Defaults to false if the result is invalid or not a boolean.
     * Optionally sets the result to a field in the input object.
     */
    execBool: (obj, formula, NAValue, setToField) => {
        const res = runParser(obj, formula, NAValue);
        let value = false;

        if (res && typeof res.result === 'boolean') {
            value = res.result;
        }

        if (setToField && obj) {
            obj[setToField] = value;
        }

        return value;
    }
};
